// src/components/Dashboard.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { signOut } from 'firebase/auth';
import { auth } from '../helper/firebase';
import ViewProfile from './ViewProfile';
import ApplyPopUp from './ApplyPopUp';
import FAQ from './FAQ';
import ReactConfetti from 'react-confetti';
import VoterRegistrationPopup from './VoterRegistrationPopup';
import NotificationPopup from './dashboard/NotificationPopup';
import {
  WIDGETS,
  widthFor,
  spanClass,
  resolveFallbackLayout,
  applyPaymentsOverride,
} from './dashboard/widgetRegistry';
import DashboardHeader from './dashboard/DashboardHeader';
import { triggerConfetti } from './dashboard/confetti';
import {
  VOTER_REG_MEDIUM_AUTO_POPUP,
  VOTER_REG_MEDIUM_DASHBOARD_LINK
} from '../utils/voterRegistrationUtils';

export default function Dashboard({ uid, dj, setDj, setElectionStatus }) {
  //Elections
  const [elections, setElections] = useState([]);
  const [showReferralNotification, setShowReferralNotification] = useState(false);
  const [showWelcomeNotification, setShowWelcomeNotification] = useState(true);
  const [statusData, setStatusData] = useState([]);
  const [assignmentConfirmLoading, setAssignmentConfirmLoading] = useState({});
  const [assignmentConfirmMessage, setAssignmentConfirmMessage] = useState({});
  const [assignmentRejectLoading, setAssignmentRejectLoading] = useState({});
  const [assignmentRejectMessage, setAssignmentRejectMessage] = useState({});
  const [dashboardLayout, setDashboardLayout] = useState(null);
  // null while in flight, 'error' if the request failed, otherwise a map of
  // election status -> {label, content, tone} | null.
  const [nextSteps, setNextSteps] = useState(null);

  // Fetch status data from the backend
  useEffect(() => {
    const fetchStatusData = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getStatus`);
        
        // Ensure statusData is an array
        if (res.data && res.data.status && Array.isArray(res.data.status)) {
          setStatusData(res.data.status);
        } else if (Array.isArray(res.data)) {
          setStatusData(res.data);
        } else {
          console.error('Status data is not in expected format:', res.data);
          setStatusData([]);
        }
      } catch (error) {
        console.error('Error fetching status data:', error);
        setStatusData([]);
      }
    };
    
    fetchStatusData();
  }, []);

  // Widget order, width and per-status visibility come from Quickbase. An error
  // falls back to the layout as it was hardcoded here before; an empty response
  // does NOT - "no widgets" is a valid answer for a DJ early in the funnel, and
  // treating it as a failure would hand them the full signed-DJ dashboard.
  const electionStatus = elections[0]?.dj_elections?.[0]?.election_status;
  useEffect(() => {
    if (!electionStatus) {
      setDashboardLayout(resolveFallbackLayout(electionStatus));
      return;
    }
    let cancelled = false;
    const fetchDashboardLayout = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getDashboardLayout`, {
          params: { status: electionStatus },
        });
        if (!cancelled) setDashboardLayout(res.data.layout || []);
      } catch (error) {
        console.error('Error fetching dashboard layout, using hardcoded fallback:', error);
        if (!cancelled) setDashboardLayout(resolveFallbackLayout(electionStatus));
      }
    };
    fetchDashboardLayout();
    return () => { cancelled = true; };
  }, [electionStatus]);

  /*
    Next Steps copy comes from Quickbase too, per status. The panel renders
    inside elections.map and a DJ looking at past elections can have several at
    different statuses, so this fetches every status on screen - but only once
    each, however many elections share it. The election a row is shown against
    no longer changes the answer; it only decides what {{election_date}}
    resolves to, which happens in the panel.

    Two things this must NOT do. It must not gate rendering the way the layout
    fetch does - elections-section is always visible, so a second gate would
    blank the whole dashboard for an extra round trip. And it must not depend on
    the `elections` array, which is replaced by several different handlers; the
    key below changes only when the set of statuses does.

    N requests share one whole-table cache on the backend, so this costs one
    Quickbase read no matter how many statuses a DJ has.
  */
  const nextStepsKey = [...new Set(elections
    .filter((election) => election.has_applied)
    // An applied election with no status can't match a row - don't spend a
    // request to be told so.
    .map((election) => election.dj_elections?.[0]?.election_status)
    .filter(Boolean))].join(',');
  useEffect(() => {
    if (!nextStepsKey) {
      setNextSteps({});
      return;
    }
    let cancelled = false;
    const fetchNextSteps = async () => {
      const statuses = nextStepsKey.split(',');
      try {
        const entries = await Promise.all(statuses.map(async (status) => {
          const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getNextSteps`, {
            params: { status },
          });
          return [status, res.data.next_steps];
        }));
        if (!cancelled) setNextSteps(Object.fromEntries(entries));
      } catch (error) {
        console.error('Error fetching next steps, using hardcoded fallback:', error);
        if (!cancelled) setNextSteps('error');
      }
    };
    fetchNextSteps();
    return () => { cancelled = true; };
  }, [nextStepsKey]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Profile state
  const [showProfile, setShowProfile] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);

  // The name in the header greeting: stage name, else first name, else none.
  const stageName = dj?.stage_name || dj?.first_name || '';

  // Apply popup state
  const [isApplyPopupOpen, setIsApplyPopupOpen] = useState(false);
  const [selectedElectionId, setSelectedElectionId] = useState(null);

  // Confetti state
  const [showConfetti, setShowConfetti] = useState(false);

  // Voter registration popup state. Holds the election and entry point the DJ
  // came from so the VoteAmerica submission can be tagged with both; null when
  // the popup is closed.
  const [voterRegistrationContext, setVoterRegistrationContext] = useState(null);

  // Open the polling place tool in a popup, keeping the DJ on the hub
  const handleOpenVoterRegistration = (election) => {
    setVoterRegistrationContext({
      election,
      medium: VOTER_REG_MEDIUM_DASHBOARD_LINK,
      tool: 'locate'
    });
  };
  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };



  // Add state to track election context
  const [electionContext, setElectionContext] = useState({
    activeAndHiring: [],
    activeNotHiring: [],
    inactive: [],
    currentState: 'loading' // 'active-hiring', 'active-not-hiring', 'inactive', 'none'
  });

  useEffect(() => {
    console.log("Fetching elections for DJ RID:", dj.rid);
    const getElectionData = async () => {
        let elections;
        let allElections = [];
        try {
          //check to see if they have a dj-election record for each election, if they do, mark that in the object
          const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/elections?dj_rid=${dj.rid}`);
          if (res.data){
            allElections = res.data;
            console.log("All elections:", allElections)
            // Categorize elections by state
            const activeAndHiring = allElections.filter(e => e.active && e.hiring === "yes");
            const activeNotHiring = allElections.filter(e => e.active && e.hiring === "no");
            const inactive = allElections.filter(e => !e.active);
            
            // Determine what to show based on priority
            let currentState = 'none';
            if(activeAndHiring.length > 0){
              elections = activeAndHiring;
              currentState = 'active-hiring';
            } else if(activeNotHiring.length > 0){
              elections = activeNotHiring;
              currentState = 'active-not-hiring';
            } else if(inactive.length > 0){
              // Only show inactive elections where user has applied/has dj_election records
              const inactiveWithApplications = inactive.filter(e => e.has_applied || e.dj_elections?.length > 0);
              if(inactiveWithApplications.length > 0) {
                elections = inactiveWithApplications.slice(0, 5); // Show more since these are their applications
                currentState = 'inactive';
              } else {
                elections = [];
                currentState = 'none';
              }
            } else {
              elections = [];
              currentState = 'none';
            }
            console.log("currentState:", currentState)
            // Update election context
            setElectionContext({
              activeAndHiring,
              activeNotHiring,
              inactive,
              currentState
            });
          }
          
          console.log("elections:", elections);
          setElections(elections);
          
          // Check if user has "Hired" status and show voter registration popup
          if (elections?.[0]?.dj_elections?.[0]?.election_status === 'Hired') {
            setVoterRegistrationContext({
              election: elections[0],
              medium: VOTER_REG_MEDIUM_AUTO_POPUP
            });
          }
        } catch (error) {
          console.error('Error fetching election data:', error);
        }
      }
    
    getElectionData();
  }, [dj.rid]);

  // The header lives above this component in App, so it has no access to the
  // election data fetched here. Report the current status up so header buttons
  // can be gated on where the DJ is in the funnel.
  useEffect(() => {
    setElectionStatus?.(elections?.[0]?.dj_elections?.[0]?.election_status || null);
  }, [elections, setElectionStatus]);

  const [isWaving, setIsWaving] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsWaving(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Confetti test function
  useEffect(() => {
    window.testConfetti = () => {
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
      }, 10000);
    };
    
    // Cleanup
    return () => {
      delete window.testConfetti;
    };
  }, []);

  // Handle view profile click
  const handleViewProfile = () => {
    console.log("View dj")
    setShowProfile(!showProfile);
  };

  // Apply to election handler
// In Dashboard.js
const handleApplyToElection = (electionId) => {

  // setElections((prev) =>
  //   prev.map((e) =>
  //     e.rid === electionId
  //       ? { ...e, has_applied: true, status: 'Applied' }
  //       : e
  //   )
  // );

 
  setSelectedElectionId(electionId);
  setIsApplyPopupOpen(true);
};

  

  const handlePopupClose = async (result = { success: false }) => {
    setIsApplyPopupOpen(false);
    
    if (result.success) {
      setElections((prevElections) =>
      prevElections.map((election) =>
        election.rid === selectedElectionId
          ? { ...election, has_applied: true, dj_elections: [{ election_status: 'Applied' }] }
          : election
      )
    );
      setShowConfetti(true);
      // Hide confetti after 5 seconds
      setTimeout(() => {
        setShowConfetti(false);
      }, 10000);


    }
    
    // Refresh only the elections data
    // try {
    //   const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/elections?dj_rid=${dj.rid}`);
    //   if (res.data) {
    //     setElections(res.data);
    //   }
    // } catch (error) {
    //   console.error('Error fetching election data:', error);
    // }
  };
  
  // cYgDs + nuqOIIsvV5ddDWwHIEl6RppE98q2

  const handleReferralClick = () => {
    const baseCode = 'cYgDs';
    const referralCode = baseCode + dj.firebase_uid;
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    
    setShowReferralNotification(true);
    setTimeout(() => setShowReferralNotification(false), 10000);
  };

  // Handle individual shift confirmation
  const handleConfirmShift = async (assignment) => {
    if (assignmentConfirmLoading[assignment.rid]) return;
    
    setAssignmentConfirmLoading(prev => ({ ...prev, [assignment.rid]: true }));
    setAssignmentConfirmMessage(prev => ({ ...prev, [assignment.rid]: '' }));

    try {
      // Persist the confirmation to the backend. The response is unused, but this
      // POST is the actual save — do not remove it as "dead" just because nothing
      // reads the result.
      const updatedAssignment = { ...assignment, dj_confirmation_shift: true };
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/saveAssignment`, {
        assignments: [updatedAssignment]
      });

      // Update the local state to reflect the change
      setElections(prevElections => {
        return prevElections.map(election => {
          if (election.dj_elections && election.dj_elections.length > 0) {
            return {
              ...election,
              dj_elections: election.dj_elections.map(djElection => {
                if (djElection.active_assignments) {
                  return {
                    ...djElection,
                    active_assignments: djElection.active_assignments.map(activeAssignment => 
                      activeAssignment.rid === assignment.rid 
                        ? { ...activeAssignment, dj_confirmation_shift: true } 
                        : activeAssignment
                    )
                  };
                }
                return djElection;
              })
            };
          }
          return election;
        });
      });
      
      setAssignmentConfirmMessage(prev => ({ 
        ...prev, 
        [assignment.rid]: 'Set confirmed successfully!' 
      }));
      triggerConfetti();
    } catch (error) {
      console.error('Error confirming shift:', error);
      setAssignmentConfirmMessage(prev => ({ 
        ...prev, 
        [assignment.rid]: 'Failed to confirm shift. Please try again.' 
      }));
    } finally {
      setAssignmentConfirmLoading(prev => ({ ...prev, [assignment.rid]: false }));
    }
  };

  const handleRejectShift = async (assignment) => {
    if (assignmentRejectLoading[assignment.rid]) return;
    
    setAssignmentRejectLoading(prev => ({ ...prev, [assignment.rid]: true }));
    setAssignmentRejectMessage(prev => ({ ...prev, [assignment.rid]: '' }));

    try {
      // Persist the rejection to the backend. The response is unused, but this POST
      // is the actual save — do not remove it as "dead".
      const updatedAssignment = { ...assignment, dj_rejected_shift: true };
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/saveAssignment`, {
        assignments: [updatedAssignment]
      });

      // Update the local state to reflect the change
      setElections(prevElections => {
        return prevElections.map(election => {
          if (election.dj_elections && election.dj_elections.length > 0) {
            return {
              ...election,
              dj_elections: election.dj_elections.map(djElection => {
                if (djElection.active_assignments) {
                  return {
                    ...djElection,
                    active_assignments: djElection.active_assignments.map(activeAssignment => 
                      activeAssignment.rid === assignment.rid 
                        ? { ...activeAssignment, dj_rejected_shift: true } 
                        : activeAssignment
                    )
                  };
                }
                return djElection;
              })
            };
          }
          return election;
        });
      });
      
      setAssignmentRejectMessage(prev => ({
        ...prev,
        [assignment.rid]: 'Set rejected successfully!'
      }));
    } catch (error) {
      console.error('Error rejecting shift:', error);
      setAssignmentRejectMessage(prev => ({
        ...prev,
        [assignment.rid]: 'Failed to reject shift. Please try again.'
      }));
    } finally {
      setAssignmentRejectLoading(prev => ({ ...prev, [assignment.rid]: false }));
    }
  };

  // Helper function to render appropriate message based on election state
  const renderElectionStateMessage = () => {
    switch(electionContext.currentState) {
      case 'none':
        return (
          <div className="min-h-screen bg-gray-50 relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
              {/* Header */}
              <DashboardHeader
                stageName={stageName}
                isWaving={isWaving}
                onReferralClick={handleReferralClick}
                showReferralNotification={showReferralNotification}
                onFAQClick={() => setShowFAQ(true)}
                onProfileClick={handleViewProfile}
                showProfile={showProfile}
                onLogout={handleLogout}
              />

              {/* Profile or No Elections Message */}
              {showProfile ? (
                <ViewProfile dj={dj} setDj={setDj} />
              ) : (
                <div className="flex items-center justify-center py-32">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">No Elections Available</h2>
                    <p className="text-gray-600">We are not currently hiring for any elections. Check back later!</p>
                  </div>
                </div>
              )}
            </div>

            {/* FAQ Modal */}
            {showFAQ && <FAQ isOpen={showFAQ} onClose={() => setShowFAQ(false)} />}
          </div>
        );
      default:
        return null;
    }
  };

  // Show loading spinner while fetching elections
  if (electionContext.currentState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (elections.length === 0) {
    return renderElectionStateMessage();
  }

  // Helper function to check if user has specific status
  const hasStatus = (requiredStatuses) => {
    const currentStatus = elections[0]?.dj_elections?.[0]?.election_status;
    return requiredStatuses.includes(currentStatus);
  };

  // Quick Stats is not a configurable widget - it sits above the widget grid and
  // keeps its own status gate. Every other widget's visibility comes from the
  // Quickbase layout resolved in dashboardLayout.
  const showQuickStats = hasStatus(['Hired', 'Contract Signed', 'Assignment Matched', 'Assignment Confirmed']);

  const djElection = elections[0]?.dj_elections?.[0];

  // Everything the widget registry's render functions can draw on, assembled
  // once so individual widgets do not each need prop wiring at the call site.
  const widgetContext = {
    elections,
    setElections,
    dj,
    setDj,
    djElection,
    electionContext,
    handleApplyToElection,
    handleOpenVoterRegistration,
    handleConfirmShift,
    handleRejectShift,
    assignmentConfirmLoading,
    assignmentConfirmMessage,
    assignmentRejectLoading,
    assignmentRejectMessage,
    nextSteps,
  };

  // Nothing renders until the layout resolves - showing the fallback first
  // would flash a different set of widgets on every load.
  const widgetLayout = dashboardLayout
    ? applyPaymentsOverride(dashboardLayout, djElection)
    : [];

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Confetti overlay */}
      {showConfetti && (
        <ReactConfetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={800}
          gravity={0.08}
          initialVelocityY={15}
          confettiSource={{
            x: 0,
            y: 0,
            w: window.innerWidth,
            h: 0
          }}
          tweenDuration={5000}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}
      {/* Welcome Notification Popup */}
      {showWelcomeNotification && (
        <NotificationPopup 
          isOpen={showWelcomeNotification}
          onClose={() => setShowWelcomeNotification(false)}
          message="Welcome to DJs at the Polls! Your dashboard shows your upcoming assignments and important notifications." //no longer used but if they want a defulat popup message we can update here
          actionText="View Details"
          statusData={statusData}
          elections={elections}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Header */}
        <DashboardHeader
          stageName={stageName}
          isWaving={isWaving}
          onReferralClick={handleReferralClick}
          showReferralNotification={showReferralNotification}
          onFAQClick={() => setShowFAQ(true)}
          onProfileClick={handleViewProfile}
          showProfile={showProfile}
          onLogout={handleLogout}
        />

        {/* Quick Stats - Hidden on mobile, visible from md breakpoint up */}
        {showQuickStats && (
          <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Upcoming Sets</p>
                  <p className="text-xl font-semibold text-gray-900">{dj.upcoming_sets || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 text-green-600"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
  <path d="M17 5H9.5C7.567 5 6 6.567 6 8.5V8.5C6 10.433 7.567 12 9.5 12H14.5C16.433 12 18 13.567 18 15.5V15.5C18 17.433 16.433 19 14.5 19H6M12 3V21" />
</svg>

                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Earnings</p>
                  <p className="text-xl font-semibold text-gray-900">${dj.earnings || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Completed Sets</p>
                  <p className="text-xl font-semibold text-gray-900">{dj.completed_sets || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">DJs Referred</p>
                  <p className="text-xl font-semibold text-gray-900">{dj.bonus ? dj.bonus : 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {showProfile ? (
          <ViewProfile dj={dj} setDj={setDj} />
        ) : ( 
          <div className="grid grid-cols-12 gap-x-6 gap-y-8">
            {widgetLayout.map((widget) => {
              const entry = WIDGETS[widget.key];
              // A layout row naming a widget this build does not have is skipped
              // rather than thrown on - Quickbase can be ahead of a deploy.
              if (!entry) return null;
              // empty:hidden keeps a widget that self-hides (returns null) from
              // leaving an empty grid cell holding a column open.
              return (
                <div key={widget.key} className={`${spanClass(widthFor(widget.key, widget.width))} empty:hidden`}>
                  {entry.render(widgetContext)}
                </div>
              );
            })}
          </div>
        )}
        {showFAQ && <FAQ onClose={() => setShowFAQ(false)} dj={dj} />}
        <ApplyPopUp
          isOpen={isApplyPopupOpen}
          onClose={handlePopupClose}
          electionId={selectedElectionId}
          election={elections.find(e => e.rid === selectedElectionId)}
          dj={dj}
        />
        {voterRegistrationContext && (
          <VoterRegistrationPopup
            onClose={() => setVoterRegistrationContext(null)}
            dj={dj}
            election={voterRegistrationContext.election}
            medium={voterRegistrationContext.medium}
            tool={voterRegistrationContext.tool}
          />
        )}
      </div>

      {/* Help Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mt-12 flex justify-center">
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/20 to-blue-500/20 rounded-full blur opacity-50 group-hover:opacity-75 transition duration-300"></div>
            <div className="relative flex items-center space-x-2 sm:space-x-3 px-3 sm:px-6 py-2 sm:py-3 bg-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 whitespace-nowrap overflow-hidden">
              <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/10 text-primary">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-gray-600 font-medium text-xs sm:text-sm">Questions? Email</span>
              <a 
                href="mailto:djs@djsatthepolls.org" 
                className="text-primary hover:text-primary-dark font-semibold transition-colors duration-200 relative group-hover:underline text-xs sm:text-sm truncate"
              >
                djs@djsatthepolls.org
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}